import React from 'react';

import { Stack } from '@mui/material';
import {
  faSquareArrowUpRight,
} from '@fortawesome/pro-regular-svg-icons';
import { Controller } from 'react-hook-form';

import { OptionsAccordion } from '@v2/components/OptionsAccordion/OptionsAccordion';
import { SectionInfo } from '@components/SectionInfo';
import { useCopy } from '@hooks/copy';
import { DataList } from '../DataList';
import { useInventory } from './hooks/useInventory';
import {
  Copies,
  fields,
  getInventoryOptions,
  inventoryTypes,
} from './constants';

import {
  AdditionalOptionsTypographyStyled,
  AdvancedSectionIconStyled,
  DividerStyled,
  DividerTypographyStyled,
  SectionBoxStyled,
  SectionInnerBoxStyled,
} from '../styles';
import { InventoryProps } from './types';
import {
  InventoryListViewIncluded,
  InventoryListViewExcluded,
} from './components/InventoryListView';

export const Inventory = ({
  bundles,
  onAdvanced,
  onAdvancedCustomInventory,
  onSelect,
  info,
  isDisplay,
  ...props
}: InventoryProps) => {
  const Copy = useCopy(Copies);

  const {
    control,
    customInventoryItems,
    isCustomInventoryExpanded,
    setIsCustomInventoryExpanded,
    inventory: actualInventory,
    domain,
    selectRecommendedInventory,
    showAdvancedTargeting,
    confirmSwitchingToInventoryPreset,
  } = useInventory({ bundles, isDisplay });

  return (
    <SectionBoxStyled display="flex" flexDirection="column" {...props}>
      <DividerStyled textAlign="left">
        <DividerTypographyStyled>Inventory</DividerTypographyStyled>
      </DividerStyled>
      <SectionInnerBoxStyled>
        <SectionInfo infoBlocks={info} />
        <Controller
          control={control}
          name={fields.inventoryOption.path}
          render={({ field }) => (
            <OptionsAccordion
              {...field}
              trackingIsSetUp
              options={getInventoryOptions({
                onAdvancedCustomInventory,
                customInventoryItems,
                isCustomInventoryExpanded,
                setIsCustomInventoryExpanded,
                isDisplay,
                bundles,
                domain,
              })}
              headerTitle={Copy.inventoryLabel}
              onChange={(_, option) => {
                if (field.value === option.value) return;

                if (option.value === inventoryTypes.bundles) {
                  onSelect?.(option);
                  return;
                }

                if (isDisplay || field.value !== 'bundles') {
                  field.onChange(option.value);
                  onSelect?.(option);
                  return;
                }

                confirmSwitchingToInventoryPreset(async () => {
                  await field.onChange(option.value);
                  onSelect?.(option);
                });

                if (
                  (
                    [
                      inventoryTypes.padmanPremiumReach,
                      inventoryTypes.performanceOptimized,
                    ] as string[]
                  ).includes(option.value)
                ) {
                  selectRecommendedInventory();
                }
              }}
            />
          )}
        />
        {showAdvancedTargeting && (
          <Stack spacing={1}>
            <AdditionalOptionsTypographyStyled
              onClick={onAdvanced}
              data-testid="advanced-inventory-options-button"
            >
              <AdvancedSectionIconStyled icon={faSquareArrowUpRight} />
              {Copy.advancedInventoryOptionsLabel}
            </AdditionalOptionsTypographyStyled>
            <DataList
              singleSubField
              title="Include"
              data={actualInventory.filter(v => v.included)}
              forceRender={!actualInventory.length}
              render={v => (
                <InventoryListViewIncluded
                  data={v}
                  isEmpty={!actualInventory.length}
                  onAdvanced={onAdvanced}
                />
              )}
            />
            <DataList
              singleSubField
              title="Exclude"
              data={actualInventory.filter(v => !v.included)}
              render={v => (
                <InventoryListViewExcluded data={v} onAdvanced={onAdvanced} />
              )}
            />
          </Stack>
        )}
      </SectionInnerBoxStyled>
    </SectionBoxStyled>
  );
};
